import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Sparkles, Copy, Download } from 'lucide-react';
import { useContent } from '../hooks/useContent';
import { useServices } from '../hooks/useServices';
import { useCatalog } from '../hooks/useCatalog';
import { EMPTY_PROFILE, KINDS, MAX_BRIEF, MAX_SUBJECT, PROFILE_LIMITS, buildProfileSubject, checkInstagramUser, checkProfile, normalizeInstagramUser, checkRequest, contentStatus, costOf, creditsSummary, isWorking, kindLabel, subjectFromItem } from '../utils/content';
import { ago } from '../utils/botQuestions';

const card = 'bg-seryi-bg border border-seryi-bg-light rounded-3xl p-5 space-y-3';
const input = 'bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2 text-xs text-seryi-heading w-full';

const copy = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success('Copiado: pégalo donde vayas a publicar');
  } catch {
    toast.error('No se pudo copiar. Selecciona el texto y cópialo a mano.');
  }
};

function Item({ item, imageUrl, onDownload, onDiscard }) {
  const st = contentStatus(item.status);
  return (
    <article className="border border-seryi-bg-light rounded-2xl p-4 space-y-2" aria-label={`${kindLabel(item.kind)}: ${item.subject}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-seryi-text">
        <span><strong className="text-seryi-heading">{kindLabel(item.kind)}</strong> · {ago(item.created_at)}</span>
        <span className={st.tone}>{st.label}</span>
      </div>
      <p className="text-[11px] text-seryi-text truncate">{item.subject}</p>
      {item.status === 'listo' && item.kind === 'imagen' && (
        <>
          {imageUrl ? <img src={imageUrl} alt={`Imagen de ${item.subject}`} className="w-full max-w-sm rounded-xl" /> : <p className="text-[11px] text-seryi-text">No se pudo cargar la imagen; actualiza la página.</p>}
          <p className="text-[10px] text-seryi-text">Revisa que se vea bien antes de usarla: nada se publica solo. Si no te gusta, pide otra.</p>
        </>
      )}
      {item.status === 'listo' && item.kind !== 'imagen' && (
        <>
          <p className="text-xs text-seryi-heading whitespace-pre-wrap bg-seryi-bg-dark rounded-xl p-3">{item.result}</p>
          <p className="text-[10px] text-seryi-text">Revísalo y ajústalo a tu gusto antes de publicar: nada se publica solo.</p>
        </>
      )}
      {item.status === 'error' && <p className="text-[11px] text-rose-300">{item.error}</p>}
      <div className="flex justify-end gap-2">
        {item.status === 'listo' && item.kind === 'imagen' && (
          <button type="button" onClick={async () => { const url = await onDownload(item); if (url) window.open(url, '_blank', 'noopener'); }} className="flex items-center gap-1.5 bg-seryi-green text-seryi-bg-dark text-[11px] font-extrabold px-3 py-2 rounded-xl"><Download size={12} /> Descargar</button>
        )}
        {item.status === 'listo' && item.kind !== 'imagen' && (
          <button type="button" onClick={() => copy(item.result)} className="flex items-center gap-1.5 bg-seryi-green text-seryi-bg-dark text-[11px] font-extrabold px-3 py-2 rounded-xl"><Copy size={12} /> Copiar</button>
        )}
        {(item.status === 'listo' || item.status === 'error' || item.status === 'pendiente') && (
          <button type="button" onClick={() => onDiscard(item.id)} className="border border-seryi-bg-light text-seryi-heading text-[11px] font-bold px-3 py-2 rounded-xl">{item.status === 'pendiente' ? 'Cancelar' : 'Descartar'}</button>
        )}
      </div>
    </article>
  );
}

function ProfileAudit({ credits, busy, onRequest }) {
  const [p, setP] = useState(EMPTY_PROFILE);
  const [error, setError] = useState('');
  const [user, setUser] = useState('');
  const [scanError, setScanError] = useState('');
  const scan = async (e) => {
    e.preventDefault();
    const problem = checkInstagramUser(user, credits);
    if (problem) { setScanError(problem); return; }
    setScanError('');
    if (await onRequest({ kinds: ['perfil_scan'], subject: normalizeInstagramUser(user), brief: '' })) setUser('');
  };
  const set = (key, value) => setP((prev) => ({ ...prev, [key]: value }));
  const submit = async (e) => {
    e.preventDefault();
    const problem = checkProfile(p, credits);
    if (problem) { setError(problem); return; }
    setError('');
    if (await onRequest({ kinds: ['perfil'], subject: buildProfileSubject(p), brief: '' })) setP(EMPTY_PROFILE);
  };
  const text = (id, label, key, placeholder, rows) => (
    <div>
      <label htmlFor={id} className="block text-[11px] text-seryi-text mb-1">{label}</label>
      {rows ? <textarea id={id} rows={rows} maxLength={PROFILE_LIMITS[key]} value={p[key]} onChange={(e) => set(key, e.target.value)} placeholder={placeholder} className={input} />
        : <input id={id} maxLength={PROFILE_LIMITS[key]} value={p[key]} onChange={(e) => set(key, e.target.value)} placeholder={placeholder} className={input} />}
    </div>
  );
  return (
    <details className={card} open>
      <summary className="cursor-pointer text-sm font-black text-seryi-heading">Auditar mi perfil de Instagram (1 crédito)</summary>
      <form onSubmit={scan} aria-label="Escanear mi perfil de Instagram" className="space-y-2 mt-3">
        <p className="text-[11px] text-seryi-text">Escribe tu usuario y la app lee tu perfil público de Instagram (biografía, enlace, categoría, tus últimas publicaciones y tu foto) y lo califica sobre 100 con 12 criterios. Si no se puede leer (perfil privado), no se gasta el crédito.</p>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[12rem]">
            <label htmlFor="pa-scan-usuario" className="block text-[11px] text-seryi-text mb-1">Tu usuario de Instagram</label>
            <input id="pa-scan-usuario" maxLength={31} value={user} onChange={(e) => setUser(e.target.value)} placeholder="@minegocio" className={input} />
          </div>
          <button type="submit" disabled={busy} className="bg-seryi-green text-seryi-bg-dark text-xs font-extrabold px-4 py-2 rounded-xl disabled:opacity-50">{busy ? 'Pidiendo…' : 'Escanear y calificar'}</button>
        </div>
        {scanError && <p role="alert" className="text-xs text-rose-300">{scanError}</p>}
      </form>
      <details className="mt-4">
        <summary className="cursor-pointer text-[11px] font-bold text-seryi-text">¿Perfil privado o prefieres pegar los datos a mano?</summary>
      <form onSubmit={submit} aria-label="Pegar los datos de mi perfil" className="space-y-3 mt-3">
        <p className="text-[11px] text-seryi-text">Pega los datos de tu perfil y la IA lo califica sobre 100 con 12 criterios, te dice qué arreglar primero y te propone una biografía. No lee tu Instagram: solo analiza lo que pegues aquí.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {text('pa-usuario', 'Usuario de Instagram', 'usuario', '@minegocio')}
          {text('pa-nombre', 'Nombre del perfil (el que aparece arriba, en negrita)', 'nombre', 'Ej: Barbería Norte | Medellín')}
        </div>
        {text('pa-bio', 'Biografía', 'bio', 'Pega tu biografía tal como está hoy', 3)}
        <div className="grid sm:grid-cols-2 gap-3">
          {text('pa-enlace', 'Enlace de la biografía', 'enlace', 'Ej: wa.me/573001112233')}
          {text('pa-categoria', 'Categoría del perfil', 'categoria', 'Ej: Barbería')}
        </div>
        {text('pa-destacados', 'Destacados (los nombres)', 'destacados', 'Ej: Precios, Antes y después, Opiniones')}
        {text('pa-publicaciones', 'Textos de tus últimas 2 o 3 publicaciones', 'publicaciones', 'Pega aquí los textos de tus últimas publicaciones', 4)}
        <div className="flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-1.5 text-xs text-seryi-heading"><input type="checkbox" checked={p.foto} onChange={(e) => set('foto', e.target.checked)} /> Mi foto de perfil es clara (logo o rostro)</label>
          <label className="flex items-center gap-1.5 text-xs text-seryi-heading"><input type="checkbox" checked={p.contacto} onChange={(e) => set('contacto', e.target.checked)} /> Tengo botón de contacto</label>
          <label className="flex items-center gap-1.5 text-xs text-seryi-heading" htmlFor="pa-semana">Publico por semana
            <input id="pa-semana" type="number" min={0} max={21} value={p.porSemana} onChange={(e) => set('porSemana', e.target.value)} className={`${input} !w-16`} />
          </label>
        </div>
        {error && <p role="alert" className="text-xs text-rose-300">{error}</p>}
        <div className="flex justify-end">
          <button type="submit" disabled={busy} className="bg-seryi-green text-seryi-bg-dark text-xs font-extrabold px-4 py-2 rounded-xl disabled:opacity-50">{busy ? 'Pidiendo…' : 'Calificar mi perfil'}</button>
        </div>
      </form>
      </details>
    </details>
  );
}

export default function ContentStudioPage() {
  const { items, credits, imageUrls, loading, missing, busy, request, discard, downloadImage } = useContent();
  const { services } = useServices();
  const { products } = useCatalog();
  const [kinds, setKinds] = useState(['publicacion', 'estado']);
  const [subject, setSubject] = useState('');
  const [brief, setBrief] = useState('');
  const [error, setError] = useState('');

  const options = useMemo(() => [
    ...(services || []).map((s) => ({ key: `s-${s.id ?? s.nombre}`, label: `Servicio: ${s.nombre}`, item: s })),
    ...(products || []).filter((p) => p.active !== false).slice(0, 200).map((p) => ({ key: `p-${p.id}`, label: `Producto: ${p.name}`, item: p }))
  ].filter((o) => o.label.trim().length > 10), [services, products]);

  if (missing) {
    return <div className="p-6 text-xs text-amber-300">Falta ejecutar la migración <code>20261020a_content_studio.sql</code> en Supabase para usar el estudio de contenido.</div>;
  }

  const toggle = (id) => setKinds((prev) => (prev.includes(id) ? prev.filter((k) => k !== id) : [...prev, id]));
  const submit = async (e) => {
    e.preventDefault();
    const problem = checkRequest({ kinds, subject, brief }, credits);
    if (problem) { setError(problem); return; }
    setError('');
    if (await request({ kinds, subject, brief })) { setSubject(''); setBrief(''); }
  };
  const summary = creditsSummary(credits);
  const cost = costOf(kinds);
  const working = items.filter((i) => isWorking(i.status));
  const rest = items.filter((i) => !isWorking(i.status) && i.status !== 'descartado' && i.status !== 'cancelado');

  return (
    <div className="space-y-5 max-w-4xl">
      <div>
        <h2 className="text-xl font-extrabold text-seryi-heading flex items-center gap-2"><Sparkles size={20} /> Contenido con IA</h2>
        <p className="text-xs text-seryi-text mt-1">Pide textos listos para tus redes y tu WhatsApp, escritos con la voz de tu negocio. Tú los revisas y los publicas.</p>
      </div>

      <form onSubmit={submit} className={card} aria-label="Pedir contenido">
        <h3 className="text-sm font-black text-seryi-heading">¿De qué quieres hablar?</h3>
        {summary && (
          <div aria-label="Créditos" className="bg-seryi-bg-dark rounded-2xl p-3 space-y-2">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <p className="text-xs text-seryi-heading">Te quedan <strong className="text-base">{summary.left}</strong> de {summary.limit} créditos este mes</p>
              <p className="text-[11px] text-seryi-text">Se renuevan el {summary.renewal}</p>
            </div>
            <div className="w-full bg-seryi-bg rounded-full h-2 overflow-hidden" role="progressbar" aria-label="Créditos usados" aria-valuenow={summary.percent} aria-valuemin={0} aria-valuemax={100}>
              <div className={`h-full rounded-full ${summary.left === 0 ? 'bg-rose-500' : summary.percent >= 80 ? 'bg-amber-500' : 'bg-seryi-green'}`} style={{ width: `${summary.percent}%` }} />
            </div>
            <p className="text-[11px] text-seryi-text">Usaste {summary.used}. Con lo que queda alcanza para {summary.images} {summary.images === 1 ? 'imagen' : 'imágenes'} o {summary.texts} {summary.texts === 1 ? 'texto' : 'textos'} (cada texto cuesta 1 crédito y cada imagen 3).</p>
          </div>
        )}
        {options.length > 0 && (
          <div>
            <label htmlFor="ct-pick" className="block text-[11px] text-seryi-text mb-1">Elegir de mi catálogo (opcional)</label>
            <select id="ct-pick" className={input} defaultValue="" onChange={(e) => { const o = options.find((x) => x.key === e.target.value); if (o) setSubject(subjectFromItem(o.item) || o.item.name || o.item.nombre || ''); }}>
              <option value="">— Escribir mi propio tema —</option>
              {options.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
          </div>
        )}
        <div>
          <label htmlFor="ct-subject" className="block text-[11px] text-seryi-text mb-1">Tema</label>
          <textarea id="ct-subject" rows={3} maxLength={MAX_SUBJECT} value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Ej: Corte clásico con lavado incluido, $35.000" className={input} />
        </div>
        <div>
          <label htmlFor="ct-brief" className="block text-[11px] text-seryi-text mb-1">Indicaciones (opcional)</label>
          <input id="ct-brief" maxLength={MAX_BRIEF} value={brief} onChange={(e) => setBrief(e.target.value)} placeholder="Ej: para el fin de semana, tono más divertido" className={input} />
        </div>
        <fieldset>
          <legend className="text-[11px] text-seryi-text mb-1">Qué quieres crear (los textos cuestan 1 crédito)</legend>
          <div className="flex flex-wrap gap-2">
            {KINDS.map((k) => (
              <label key={k.id} title={k.hint} className={`flex items-center gap-1.5 text-xs px-3 py-2 rounded-xl border cursor-pointer ${kinds.includes(k.id) ? 'border-seryi-green text-seryi-heading' : 'border-seryi-bg-light text-seryi-text'}`}>
                <input type="checkbox" checked={kinds.includes(k.id)} onChange={() => toggle(k.id)} /> {k.label}
              </label>
            ))}
          </div>
        </fieldset>
        {error && <p role="alert" className="text-xs text-rose-300">{error}</p>}
        <div className="flex flex-wrap items-center justify-end gap-3">
          {kinds.length > 0 && <p className="text-[11px] text-seryi-text" aria-label="Costo">Esto cuesta {cost} {cost === 1 ? 'crédito' : 'créditos'}</p>}
          <button type="submit" disabled={busy} className="bg-seryi-green text-seryi-bg-dark text-xs font-extrabold px-4 py-2 rounded-xl disabled:opacity-50">{busy ? 'Pidiendo…' : `Crear ${kinds.length || ''} pieza${kinds.length === 1 ? '' : 's'}`}</button>
        </div>
      </form>

      <ProfileAudit credits={credits} busy={busy} onRequest={request} />

      {loading && <p className="text-xs text-seryi-text">Cargando…</p>}
      {working.length > 0 && (
        <section className={card} aria-label="En proceso">
          <h3 className="text-sm font-black text-seryi-heading">En proceso</h3>
          {working.map((i) => <Item key={i.id} item={i} imageUrl={imageUrls?.[i.id]} onDownload={downloadImage} onDiscard={discard} />)}
        </section>
      )}
      {rest.length > 0 && (
        <section className={card} aria-label="Tus textos">
          <h3 className="text-sm font-black text-seryi-heading">Tus textos</h3>
          {rest.map((i) => <Item key={i.id} item={i} imageUrl={imageUrls?.[i.id]} onDownload={downloadImage} onDiscard={discard} />)}
        </section>
      )}
      {!loading && items.length === 0 && <p className="text-xs text-seryi-text">Todavía no has pedido contenido. Elige un tema arriba y crea tu primer texto.</p>}
    </div>
  );
}
